/**
 * BB-SOUND.JS - Sound System Module
 * Manages audio playback with tier-based variants
 * Uses Web Audio API for robust mobile support
 * 
 * @version 2.0.0
 * @requires window.BundleBuilderSounds config object
 */

window.BB = window.BB || {};

window.BB.Sound = (function() {
  'use strict';
  
  var VERSION = '2.0.0';
  var sounds = {};           // { 'card-select-1': [AudioBuffer, ...] }
  var config = window.BundleBuilderSounds || { enabled: false };
  var loaded = false;
  var initializing = false;
  var userInteracted = false;
  var cachedSession = false;
  
  // Web Audio Context
  var AudioContext = window.AudioContext || window.webkitAudioContext;
  var context = null;
  var gainNode = null;
  
  // Mobile detection
  var isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Sounds to skip on mobile devices (rapid tick sounds can be jarring)
  // Note: ambient-loop removed from blocklist - Web Audio volume control works on iOS
  var mobileBlockList = ['number-tick'];
  
  // Check cache
  function checkCachedSession() {
    try {
      var cached = localStorage.getItem('bb_sounds_cached');
      var cacheTime = localStorage.getItem('bb_sounds_cache_time');
      if (cached === 'true' && cacheTime) {
        var age = Date.now() - parseInt(cacheTime);
        if (age < 24 * 60 * 60 * 1000) {
          cachedSession = true;
          return true;
        }
      }
    } catch(e) {}
    return false;
  }
  
  function markCached() {
    try {
      localStorage.setItem('bb_sounds_cached', 'true');
      localStorage.setItem('bb_sounds_cache_time', Date.now().toString());
    } catch(e) {}
  }
  
  checkCachedSession();
  
  var currentTier = 1;
  var volumeMultipliers = {};
  var loadingProgress = 0;
  var loadingCallbacks = [];
  
  var effectsVolume = 1.0;
  var musicVolume = isMobile ? 0.3 : 0.5; // 30% on mobile, 50% on desktop
  var effectsEnabled = true;
  var musicEnabled = true;
  var allSoundEnabled = true;
  
  // Log mobile detection (music now enabled on mobile - Web Audio volume works on iOS)
  if (isMobile) {
    console.log('📱 Mobile detected - Web Audio API enabled, music volume default 30%');
  }
  
  // ========================================================================
  // AUDIO CONTEXT MANAGEMENT
  // ========================================================================
  
  function initContext() {
    if (context) return;
    try {
      context = new AudioContext();
      gainNode = context.createGain();
      gainNode.connect(context.destination);
      updateMasterVolume();
    } catch(e) {
      console.warn('🔇 Web Audio API not supported:', e);
    }
  }
  
  function unlockAudio() {
    if (userInteracted) return;
    userInteracted = true;
    
    // Initialize if not already done
    initContext();
    
    // Resume context (required for iOS)
    if (context && context.state === 'suspended') {
      context.resume().then(function() {
        console.log('🔓 Audio Context resumed via user interaction');
      });
    }
    
    // Play silent buffer to warm up
    try {
      if (context) {
        var buffer = context.createBuffer(1, 1, 22050);
        var source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);
        source.start(0);
      }
    } catch(e) {}
  }
  
  ['click', 'touchstart', 'touchend', 'mousedown', 'keydown'].forEach(function(eventType) {
    document.addEventListener(eventType, unlockAudio, { once: true, passive: true });
  });
  
  function forceUnlock() {
    unlockAudio();
  }
  
  // ========================================================================
  // LOADING
  // ========================================================================
  
  function init() {
    if (!config.enabled || loaded || initializing) return;
    
    // iOS: Skip sound loading entirely to prevent memory crashes
    if (isMobile && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      console.log('📱 [BB.Sound] Skipping sound loading on iOS to prevent memory issues');
      loaded = true;
      setLoadingProgress(100);
      return;
    }
    
    initializing = true;
    // #region agent log - H8 Sound system init tracking
    if (window._bbDebugLog) window._bbDebugLog('bb-sound.js:init', 'SoundSystem init started', { strapiUrl: !!config.strapiUrl, baseUrl: !!config.baseUrl }, 'H8');
    // #endregion
    initContext();
    
    if (config.strapiUrl) {
      initFromStrapi();
    } else if (config.baseUrl) {
      initFromFolder();
    } else {
      setLoadingProgress(100);
    }
  }
  
  function setLoadingProgress(progress) {
    loadingProgress = Math.min(100, Math.max(0, progress));
    loadingCallbacks.forEach(function(cb) {
      try { cb(loadingProgress); } catch(e) {}
    });
  }
  
  function onLoadingProgress(callback) {
    loadingCallbacks.push(callback);
    callback(loadingProgress);
  }
  
  function loadBuffer(url, callback) {
    if (!context) initContext();
    if (!context) {
      if (callback) callback(null);
      return;
    }
    
    fetch(url)
      .then(function(response) { return response.arrayBuffer(); })
      .then(function(arrayBuffer) {
        return context.decodeAudioData(arrayBuffer);
      })
      .then(function(audioBuffer) {
        if (callback) callback(audioBuffer);
      })
      .catch(function(e) {
        console.warn('Failed to load sound:', url, e);
        if (callback) callback(null);
      });
  }
  
  function initFromStrapi() {
    var strapiUrl = config.strapiUrl.replace(/\/$/, '');
    var headers = {};
    if (config.strapiApiToken) headers['Authorization'] = 'Bearer ' + config.strapiApiToken;
    
    setLoadingProgress(10);
    
    fetch(strapiUrl + '/api/upload/files?pagination[limit]=500', { headers: headers })
      .then(function(res) { return res.json(); })
      .then(function(files) {
        var pattern = /^(card[_-]select|card[_-]deselect|physical[_-]add|physical[_-]remove|goodies[_-]add|goodies[_-]remove|checkout[_-]click|spark[_-]burst|checkmark[_-]land|tier[_-]unlock|upgrade[_-]slam|upgrade[_-]add|hover[_-]card|button[_-]click|ambient[_-]loop|progress[_-]grow|deck[_-]light|number[_-]tick)[_-]tier[_-](\d+)[_-](\d+)x\./;
        
        // Filter files that match the sound naming pattern
        // Note: Mobile now loads all tiers - modern devices have sufficient memory
        // and users want full tier progression sounds
        var soundFiles = files.filter(function(f) { 
          return pattern.test(f.name);
        });
        
        var loadedCount = 0;
        var totalToLoad = soundFiles.length || 1;
        
        setLoadingProgress(40);
        
        if (soundFiles.length === 0) {
          loaded = true;
          setLoadingProgress(100);
          return;
        }
        
        // Debug: Log what files matched the pattern
        if (window.BB_SOUND_DEBUG) {
          console.log('[BB.Sound] Strapi returned', files.length, 'total files');
          console.log('[BB.Sound] Pattern matched', soundFiles.length, 'sound files');
          var tierCounts = {};
          soundFiles.forEach(function(f) {
            var m = f.name.match(pattern);
            var t = parseInt(m[2]);
            tierCounts[t] = (tierCounts[t] || 0) + 1;
          });
          console.log('[BB.Sound] Files per tier:', tierCounts);
        }
        
        // #region agent log - H8 Sound decode tracking
        if (window._bbDebugLog) window._bbDebugLog('bb-sound.js:initFromStrapi', 'Starting decode of ' + totalToLoad + ' sounds', { count: totalToLoad }, 'H8');
        var decodeStartTime = Date.now();
        // #endregion
        
        soundFiles.forEach(function(file) {
          var match = file.name.match(pattern);
          var action = match[1].replace(/_/g, '-');
          var tier = parseInt(match[2]);
          var key = action + '-' + tier;
          var url = file.url.startsWith('http') ? file.url : strapiUrl + file.url;
          
          loadBuffer(url, function(buffer) {
            if (buffer) {
              if (!sounds[key]) sounds[key] = [];
              sounds[key].push(buffer);
            }
            
            loadedCount++;
            var progress = 40 + Math.floor((loadedCount / totalToLoad) * 60);
            setLoadingProgress(progress);
            
            // #region agent log - H8 Sound decode progress (every 10 sounds)
            if (loadedCount % 10 === 0 && window._bbDebugLog) {
              window._bbDebugLog('bb-sound.js:decode', 'Decoded ' + loadedCount + '/' + totalToLoad, { 
                loaded: loadedCount, 
                total: totalToLoad,
                elapsed: Date.now() - decodeStartTime
              }, 'H8');
            }
            // #endregion
            
            if (loadedCount >= totalToLoad) {
              loaded = true;
              setLoadingProgress(100);
              markCached();
              // #region agent log - H8 Sound decode complete
              if (window._bbDebugLog) window._bbDebugLog('bb-sound.js:complete', 'All sounds decoded', { 
                groups: Object.keys(sounds).length,
                totalTime: Date.now() - decodeStartTime 
              }, 'H8');
              // #endregion
              console.log('🔊 Web Audio: Loaded ' + Object.keys(sounds).length + ' sound groups');
              
              // Debug: List all loaded sound keys with variant counts
              if (window.BB_SOUND_DEBUG) {
                var summary = {};
                Object.keys(sounds).forEach(function(k) {
                  summary[k] = sounds[k].length + ' variants';
                });
                console.log('[BB.Sound] Loaded sounds:', summary);
              }
            }
          });
        });
      })
      .catch(function(err) {
        console.warn('🔇 Strapi load failed:', err);
        setLoadingProgress(100);
      });
  }
  
  function initFromFolder() {
    var baseUrl = config.baseUrl.replace(/\/?$/, '/');
    if (baseUrl.includes('dropbox.com')) {
      baseUrl = baseUrl.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '').replace('?dl=1', '');
    }
    
    var actions = config.actions || [];
    var maxTiers = 14; // All tiers available on all devices
    var maxVariants = 5;
    
    // Just mark complete for folder mode (legacy/dev)
    loaded = true;
    setLoadingProgress(100);
  }
  
  // ========================================================================
  // PLAYBACK
  // ========================================================================
  
  function play(action, tierOrVolume, volumeMultiplier) {
    if (!config.enabled || !allSoundEnabled) return;
    if (!effectsEnabled) return;
    
    // Skip blocked sounds on mobile (e.g. number ticks)
    if (isMobile && mobileBlockList.indexOf(action) !== -1) {
      return; 
    }

    if (!context) initContext();
    if (context.state === 'suspended') context.resume();
    
    // Parse arguments
    var tier = currentTier;
    var volume = 1;
    var tierSource = 'currentTier';
    
    // Handle explicit tier override: play('action', tierNumber, volume)
    // Tier numbers are 1-14, so check for integer >= 1
    if (typeof tierOrVolume === 'number' && Number.isInteger(tierOrVolume) && tierOrVolume >= 1) {
      tier = tierOrVolume;
      tierSource = 'explicit';
      volume = volumeMultiplier || 1;
    } 
    // Handle volume-only: play('action', volumeMultiplier) where 0 < vol <= 1
    else if (typeof tierOrVolume === 'number' && tierOrVolume > 0 && tierOrVolume <= 1) {
      volume = tierOrVolume;
    }
    // Handle null/undefined tier with volume: play('action', null, volume)
    else if (volumeMultiplier !== undefined) {
      volume = volumeMultiplier;
    }
    
    // Note: Mobile now uses full tier system - no fallback to tier 1
    
    var requestedKey = action + '-' + tier;
    var key = requestedKey;
    var variants = sounds[key];
    var usedFallback = false;
    
    // Fallback logic - if no sound for requested tier, try tier 1
    if (!variants || variants.length === 0) {
      key = action + '-1';
      variants = sounds[key];
      usedFallback = true;
    }
    
    // Mapping fallbacks for physical/goodies actions
    if (!variants || variants.length === 0) {
      if (action === 'physical-add' || action === 'goodies-add') {
        variants = sounds['card-select-' + tier] || sounds['card-select-1'];
        key = variants ? ('card-select-' + (sounds['card-select-' + tier] ? tier : '1')) : key;
      }
      if (action === 'physical-remove' || action === 'goodies-remove') {
        variants = sounds['card-deselect-' + tier] || sounds['card-deselect-1'];
        key = variants ? ('card-deselect-' + (sounds['card-deselect-' + tier] ? tier : '1')) : key;
      }
    }
    
    // Debug logging (only in development/debug mode)
    if (window.BB_SOUND_DEBUG) {
      console.log('[BB.Sound] play() - action:', action, 
        '| requested tier:', tier, '(' + tierSource + ')',
        '| key:', requestedKey,
        '| found:', !!variants,
        usedFallback ? '| FALLBACK to: ' + key : '',
        '| available keys:', Object.keys(sounds).filter(function(k) { return k.startsWith(action); }).join(', ')
      );
    }
    
    if (!variants || variants.length === 0) return;
    
    // Pick random variant buffer
    var buffer = variants[Math.floor(Math.random() * variants.length)];
    
    try {
      var source = context.createBufferSource();
      source.buffer = buffer;
      
      var gain = context.createGain();
      
      // Calculate volume
      var actionVol = volumeMultipliers[action] !== undefined ? volumeMultipliers[action] : 1;
      gain.gain.value = effectsVolume * actionVol * volume;
      
      source.connect(gain);
      gain.connect(gainNode); // Connect to master gain
      
      source.start(0);
    } catch(e) {
      console.warn('Play failed:', e);
    }
  }
  
  // ========================================================================
  // VOLUME & AMBIENT
  // ========================================================================
  
  function updateMasterVolume() {
    if (gainNode) {
      gainNode.gain.value = config.masterVolume || 0.5;
    }
  }
  
  function setVolume(actionOrVol, vol) {
    if (vol === undefined) {
      config.masterVolume = Math.max(0, Math.min(1, actionOrVol));
      updateMasterVolume();
    } else {
      volumeMultipliers[actionOrVol] = Math.max(0, Math.min(1, vol));
    }
  }
  
  function getVolume(action) {
    return action ? (volumeMultipliers[action] || 1) : (config.masterVolume || 0.5);
  }
  
  function setTier(tier) { 
    var oldTier = currentTier;
    currentTier = Math.max(1, Math.min(tier, 14)); 
    if (window.BB_SOUND_DEBUG && oldTier !== currentTier) {
      console.log('[BB.Sound] setTier:', oldTier, '→', currentTier);
    }
  }
  function getTier() { return currentTier; }
  
  function setEffectsVolume(vol) {
    effectsVolume = Math.max(0, Math.min(1, vol));
    try { localStorage.setItem('bb_effects_volume', Math.round(effectsVolume * 100)); } catch(e) {}
  }
  
  function getEffectsVolume() { return effectsVolume; }
  
  function toggleEffects(e) { 
    effectsEnabled = e !== undefined ? e : !effectsEnabled;
    try { localStorage.setItem('bb_effects_enabled', effectsEnabled ? '1' : '0'); } catch(e) {}
    return effectsEnabled;
  }
  
  function isEffectsEnabled() { return effectsEnabled; }

  function setMusicVolume(vol) {
    // Note: Web Audio volume control works on iOS (unlike HTML5 Audio)
    musicVolume = Math.max(0, Math.min(1, vol));
    updateAmbientVolume();
    try { localStorage.setItem('bb_music_volume', Math.round(musicVolume * 100)); } catch(e) {}
  }
  
  function getMusicVolume() { return musicVolume; }
  
  function toggleMusic(e) {
    // Note: Web Audio ambient loop works on iOS (volume control via gain node)
    musicEnabled = e !== undefined ? e : !musicEnabled;
    if (!musicEnabled) stopAmbient();
    try { localStorage.setItem('bb_music_enabled', musicEnabled ? '1' : '0'); } catch(e) {}
    return musicEnabled;
  }
  
  function isMusicEnabled() { return musicEnabled; }
  
  function toggle(enabled) {
    allSoundEnabled = enabled !== undefined ? enabled : !allSoundEnabled;
    config.enabled = allSoundEnabled;
    if (!allSoundEnabled) stopAmbient();
    try { localStorage.setItem('bb_all_sound_enabled', allSoundEnabled ? '1' : '0'); } catch(e) {}
    return allSoundEnabled;
  }
  
  // Ambient Loop (Web Audio implementation)
  var ambientSource = null;
  var ambientGain = null;
  
  function startAmbient() {
    // Note: Web Audio ambient loop now enabled on mobile - uses gain node for volume control
    if (!config.enabled || !allSoundEnabled || !musicEnabled || !context) return;
    if (ambientSource) return; // Already playing
    
    var variants = sounds['ambient-loop-1'];
    if (!variants || variants.length === 0) return;
    
    var buffer = variants[0];
    
    try {
      if (context.state === 'suspended') context.resume();
      
      ambientSource = context.createBufferSource();
      ambientSource.buffer = buffer;
      ambientSource.loop = true;
      
      ambientGain = context.createGain();
      updateAmbientVolume();
      
      ambientSource.connect(ambientGain);
      ambientGain.connect(gainNode);
      ambientSource.start(0);
      console.log('🎵 Web Audio: Ambient loop started');
    } catch(e) {}
  }
  
  function stopAmbient() {
    if (ambientSource) {
      try { ambientSource.stop(); } catch(e) {}
      ambientSource = null;
      ambientGain = null;
    }
  }
  
  function updateAmbientVolume() {
    if (ambientGain) {
      var base = 0.2;
      var actionVol = volumeMultipliers['ambient-loop'] || 1;
      ambientGain.gain.value = musicVolume * actionVol * base;
    }
  }
  
  // Persistence
  function loadSavedVolumes() {
    var savedEffects = localStorage.getItem('bb_effects_volume');
    if (savedEffects) effectsVolume = parseFloat(savedEffects) / 100;
    
    var savedMusic = localStorage.getItem('bb_music_volume');
    if (savedMusic) musicVolume = parseFloat(savedMusic) / 100;
    
    // Per-action volumes
    ['card-select', 'tier-unlock', 'ambient-loop'].forEach(function(a) {
      var v = localStorage.getItem('bb_sound_vol_' + a);
      if (v) volumeMultipliers[a] = parseFloat(v) / 100;
    });
  }
  
  if (document.readyState === 'complete') {
    loadSavedVolumes();
    init();
  } else {
    window.addEventListener('load', function() {
      loadSavedVolumes();
      init();
    });
  }
  
  // Debug utilities
  function enableDebug() {
    window.BB_SOUND_DEBUG = true;
    console.log('[BB.Sound] Debug mode ENABLED');
    console.log('[BB.Sound] Current tier:', currentTier);
    console.log('[BB.Sound] Loaded sounds:', Object.keys(sounds).length, 'groups');
    console.log('[BB.Sound] Sound keys:', Object.keys(sounds).sort().join(', '));
    return 'Debug enabled - sound actions will now be logged';
  }
  
  function disableDebug() {
    window.BB_SOUND_DEBUG = false;
    console.log('[BB.Sound] Debug mode DISABLED');
  }
  
  function getLoadedSounds() {
    var result = {};
    Object.keys(sounds).forEach(function(key) {
      result[key] = sounds[key].length;
    });
    return result;
  }
  
  // Check for debug URL param on load
  if (typeof window !== 'undefined') {
    try {
      var urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sound_debug') === 'true' || urlParams.get('debug') === 'true') {
        window.BB_SOUND_DEBUG = true;
        console.log('[BB.Sound] Debug mode enabled via URL param');
      }
    } catch(e) {}
  }
  
  return {
    version: VERSION,
    play: play,
    setTier: setTier,
    getTier: getTier,
    setVolume: setVolume,
    getVolume: getVolume,
    toggle: toggle,
    isEnabled: function() { return allSoundEnabled; },
    init: init,
    setEffectsVolume: setEffectsVolume,
    getEffectsVolume: getEffectsVolume,
    toggleEffects: toggleEffects,
    isEffectsEnabled: isEffectsEnabled,
    setMusicVolume: setMusicVolume,
    getMusicVolume: getMusicVolume,
    toggleMusic: toggleMusic,
    isMusicEnabled: isMusicEnabled,
    startAmbient: startAmbient,
    stopAmbient: stopAmbient,
    toggleAmbient: function() {
      if (ambientSource) stopAmbient(); else startAmbient();
      return !!ambientSource;
    },
    isAmbientPlaying: function() { return !!ambientSource; },
    updateAmbientVolume: updateAmbientVolume,
    onLoadingProgress: onLoadingProgress,
    isLoaded: function() { return loaded; },
    isCached: function() { return cachedSession; },
    forceUnlock: forceUnlock,
    // Debug utilities
    enableDebug: enableDebug,
    disableDebug: disableDebug,
    getLoadedSounds: getLoadedSounds,
    isMobile: function() { return isMobile; }
  };
})();

window.SoundSystem = window.BB.Sound;
